// src/hooks/useSmartPlanning.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { SmartPlanManager } from '../_utils/smartPlanManager';

/**
 * 智能规划 React Hook
 * 提供智能日期解析和行程规划功能
 */
export const useSmartPlanning = () => {
  // 状态管理
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [processingStage, setProcessingStage] = useState('idle'); // idle, parsing, fetching, transforming, complete
  const [error, setError] = useState(null);
  
  // 实例管理
  const smartPlanManagerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // 初始化管理器
  useEffect(() => {
    if (!smartPlanManagerRef.current) {
      smartPlanManagerRef.current = new SmartPlanManager();
      console.log('🔧 SmartPlanManager initialized');
    }

    // 清理函数
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * 处理用户请求的主要方法
   * @param {string} userInput - 用户输入文本
   * @param {object} context - 上下文信息
   * @returns {Promise<object>} 处理结果
   */
  const processRequest = useCallback(async (userInput, context = {}) => {
    if (!userInput || typeof userInput !== 'string') {
      throw new Error('Invalid user input');
    }

    // 重置状态
    setIsProcessing(true);
    setProcessingStage('parsing');
    setError(null);
    setLastResult(null);

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      console.log('🚀 Starting smart planning process for:', context);
      
      // 增强上下文
      const enhancedContext = {
        ...context,
        timestamp: Date.now(),
        sessionId: context.sessionId || `session_${Date.now()}`,
        abortSignal: abortControllerRef.current.signal
      };

      setProcessingStage('processing');
      
      // 调用智能规划管理器
      const result = await smartPlanManagerRef.current.processUserRequest(
        userInput, 
        enhancedContext
      );

      // 检查是否被取消
      if (abortControllerRef.current.signal.aborted) {
        throw new Error('Request was cancelled');
      }

      setProcessingStage('complete');
      setLastResult(result);
      
      console.log('✅ Smart planning completed:', result.success ? 'SUCCESS' : 'FAILED');
      
      return result;

    } catch (error) {
      console.error('❌ Smart planning error:', error);
      
      setError(error);
      
      const errorResult = {
        success: false,
        error: error.name === 'AbortError' ? 'CANCELLED' : 'PROCESSING_ERROR',
        message: error.name === 'AbortError' 
          ? '请求已取消' 
          : '处理您的请求时遇到错误，请重试。',
        details: error.message,
        timestamp: Date.now()
      };
      
      setLastResult(errorResult);
      return errorResult;
      
    } finally {
      setIsProcessing(false);
      setProcessingStage('idle');
    }
  }, []);

  /**
   * 检查输入是否包含日期/天数信息
   * @param {string} input - 用户输入
   * @returns {boolean} 是否包含日期信息
   */
  const checkIfContainsDateInfo = useCallback((input) => {
    if (!input || typeof input !== 'string') {
      return false;
    }

    try {
      return SmartPlanManager.containsDateInfo(input);
    } catch (error) {
      console.warn('Error checking date info:', error);
      return false;
    }
  }, []);

  /**
   * 快速解析输入（不调用API）
   * @param {string} input - 用户输入
   * @returns {object} 解析结果
   */
  const quickParse = useCallback((input) => {
    if (!smartPlanManagerRef.current) {
      return null;
    }

    try {
      return smartPlanManagerRef.current.dateParser.parseUserInput(input);
    } catch (error) {
      console.error('Quick parse error:', error);
      return null;
    }
  }, []);

  /**
   * 取消当前处理
   */
  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('🛑 Cancelling smart planning process');
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * 重置状态
   */
  const resetState = useCallback(() => {
    setIsProcessing(false);
    setProcessingStage('idle');
    setError(null);
    setLastResult(null);
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * 获取处理历史
   */
  const getProcessingHistory = useCallback(() => {
    // 可以从localStorage或其他存储中获取历史记录
    try {
      const history = localStorage.getItem('smart_planning_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.warn('Error loading processing history:', error);
      return [];
    }
  }, []);

  /**
   * 保存处理结果到历史
   */
  const saveToHistory = useCallback((result) => {
    if (!result || !result.success) return;

    try {
      const history = getProcessingHistory();
      const newEntry = {
        id: `entry_${Date.now()}`,
        timestamp: Date.now(),
        input: result.originalInput || '',
        days: result.parseResult?.days,
        startDate: result.parseResult?.startDate,
        endDate: result.parseResult?.endDate,
        confidence: result.parseResult?.confidence,
        success: result.success
      };

      history.unshift(newEntry);
      
      // 保持最近20条记录
      const trimmedHistory = history.slice(0, 20);
      localStorage.setItem('smart_planning_history', JSON.stringify(trimmedHistory));
      
      console.log('💾 Saved to planning history:', newEntry.id);
    } catch (error) {
      console.warn('Error saving to history:', error);
    }
  }, [getProcessingHistory]);

  /**
   * 验证日期范围合理性
   */
  const validateDateRange = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return false;

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();
      
      // 检查日期有效性
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return false;
      }

      // 检查逻辑合理性
      if (start > end) {
        return false;
      }

      // 检查是否过去的日期（允许7天内的过去日期）
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      
      if (start < sevenDaysAgo) {
        return false;
      }

      // 检查是否太远的未来（最多2年）
      const twoYearsLater = new Date(today);
      twoYearsLater.setFullYear(today.getFullYear() + 2);
      
      if (start > twoYearsLater) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Date validation error:', error);
      return false;
    }
  }, []);

  /**
   * 获取智能建议
   */
  const getSmartSuggestions = useCallback((partialInput) => {
    const suggestions = [];

    if (!partialInput) {
      return [
        '计划5天蒙特利尔之旅',
        '3天2夜周末游',
        '一周深度文化游',
        '经济型短途游'
      ];
    }

    const lowerInput = partialInput.toLowerCase();

    // 基于部分输入生成建议
    if (lowerInput.includes('蒙特利尔') || lowerInput.includes('montreal')) {
      suggestions.push(
        '5天蒙特利尔深度游',
        '3天蒙特利尔美食之旅',
        '周末蒙特利尔文化游'
      );
    }

    if (lowerInput.includes('天')) {
      const dayMatch = partialInput.match(/(\d+)天/);
      if (dayMatch) {
        const days = dayMatch[1];
        suggestions.push(
          `${days}天蒙特利尔完整行程`,
          `${days}天文化艺术游`,
          `${days}天美食探索`
        );
      }
    }

    if (lowerInput.includes('预算') || lowerInput.includes('budget')) {
      suggestions.push(
        '经济型3天游',
        '中等预算5天游',
        '豪华1周游'
      );
    }

    // 如果没有特定建议，返回通用建议
    if (suggestions.length === 0) {
      suggestions.push(
        '为我规划蒙特利尔行程',
        '5天4夜完整行程',
        '周末短途游',
        '美食文化深度游'
      );
    }

    return suggestions.slice(0, 4); // 最多返回4个建议
  }, []);

  // 自动保存成功结果
  useEffect(() => {
    if (lastResult && lastResult.success) {
      saveToHistory(lastResult);
    }
  }, [lastResult, saveToHistory]);

  // 返回Hook接口
  return {
    // 主要功能
    processRequest,
    checkIfContainsDateInfo,
    quickParse,
    
    // 状态
    isProcessing,
    processingStage,
    lastResult,
    error,
    
    // 控制方法
    cancelProcessing,
    resetState,
    
    // 工具方法
    validateDateRange,
    getSmartSuggestions,
    getProcessingHistory,
    
    // 便捷属性
    hasError: !!error,
    hasResult: !!lastResult,
    isSuccess: lastResult?.success === true,
    confidence: lastResult?.parseResult?.confidence || 0,
    
    // 调试信息（开发环境）
    ...(process.env.NODE_ENV === 'development' && {
      _internal: {
        smartPlanManager: smartPlanManagerRef.current,
        abortController: abortControllerRef.current
      }
    })
  };
};

// 默认导出
export default useSmartPlanning;


